import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  DatePicker,
  FormField,
  ScrollArea,
  Select,
  TablePage,
  TextField,
} from "@outpave/ds";
import { useInvoiceData } from "../../hooks/useInvoiceData";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  Divider,
  Tooltip,
  Typography,
} from "@mui/material";
import { Inline, Stack } from "components/layout/reusableLayouts";
import ContactSearchAutoComplete from "../../components/common/ContactSearchAutoComplete";
import SelectExpenseDrawer from "../../components/admin/invoices/SelectExpenseDrawer";
import { formatCashFull } from "../../utils/formatCash";
import { GET_TRANSACTION } from "../../graphql/transaction";
import { useLazyQuery } from "@apollo/client";
import LoadingWithoutModal from "../../components/common/LoadingWithoutModal";
import InvoicePreviewContainer from "../../components/admin/invoices/InvoicePreviewContainer";
import InvoicePreviewThumbnail from "../../components/admin/invoices/InvoicePreviewThumbnail";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import { AttachmentDrawerType } from "../../enums/AttachmentDrawerType";
import useDebouncedAutoSave from "../../hooks/useDebouncedAutoSave";
import { DEBOUNCE_TIMEOUT } from "../../components/common/Constants";
import AttachmentsComponent, {
  AttachmentRenderType,
} from "components/common/attachments/AttachmentComponent";
import { ATTACHMENT_PARENT_TYPE } from "enums/AttachmentParentType";
import { LocalImageAsset } from "hooks/useAttachmentAndAssetHandling";
import useGetParamFromUrl from "hooks/useGetParamFromUrl";
import { InvoiceStatus } from "enums/InvoiceStatus";
import { InvoiceDiscountType } from "enums/InvoiceDiscountType";
import ContactStatus from "enums/ContactStatus";
import RateOrAmountField, {
  RateOrAmountOption,
} from "components/admin/invoices/RateOrAmountField";
import useGetInvoiceStatusDetails from "hooks/useGetInvoiceStatusDetails";
import { PaymentTermsLabels } from "utils/invoices/invoiceFormat";
import { useFormikHelpers } from "hooks/useFormikHelpers";
import AllocationSearchAutoComplete from "components/common/AllocationSearchAutoComplete";
import { ImageAsset } from "components/common/receipts/ReceiptReviewDetails";
import InvoiceItemsTable from "components/admin/invoices/InvoiceItemsTable";
import FormikCustomFields from "components/common/FormikCustomFields";
import { useFeatureEntitlements } from "@frontegg/react";
import CustomPanel from "components/common/CustomPanel";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Add01Icon,
  CreditCardIcon,
  InfoIcon,
} from "@hugeicons-pro/core-stroke-rounded";

const FIELD_COLUMN_SX = {
  flexGrow: 1,
  flexBasis: "300px",
  minWidth: 0,
};

/**
 * The notes field absorbs whatever height the row has spare, so the two
 * columns stay level whichever one is taller.
 *
 * The chain is named here rather than on the field because the DS TextField
 * puts its `sx` on the input pill; the control around it and the textarea
 * inside it are both out of reach from the call site. A DS field that can fill
 * its container would retire this.
 */
const NOTES_FILL_SX = {
  flex: "1 0 auto",
  width: "100%",
  display: "flex",
  "& > .MuiFormControl-root": { flex: 1 },
  "& .MuiInputBase-root": { flex: 1, alignItems: "stretch" },
  "& textarea.MuiInputBase-input": { height: "100%", overflow: "auto" },
};

/** Names the scrim so the card can reveal it on hover and on focus. */
const PREVIEW_OVERLAY_CLASS = "invoice-preview-overlay";

const PANEL_BODY_SX = {
  padding: "var(--space-6)",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "var(--space-6)",
};

const PAYMENT_TERMS_OPTIONS = Object.entries(PaymentTermsLabels).map(
  ([value, labelKey]) => ({ value, labelKey }),
);

const rateOrAmountOptions = (t: TFunction): RateOrAmountOption[] => [
  { value: InvoiceDiscountType.PERCENTAGE, glyph: "%", name: t("Percentage") },
  { value: InvoiceDiscountType.AMOUNT, glyph: "$", name: t("Fixed Amount") },
];

/**
 * Service Fee carries a third unit the other two rows do not: CREDIT_CARD
 * passes the processing fee to the customer and computes its own amount, which
 * is why the figure field goes inert under it.
 */
const serviceFeeOptions = (t: TFunction): RateOrAmountOption[] => [
  ...rateOrAmountOptions(t),
  {
    value: InvoiceDiscountType.CREDIT_CARD,
    glyph: <HugeiconsIcon icon={CreditCardIcon} size={16} />,
    name: t("Card Fees"),
    description: t("Covers card processing"),
  },
];

const TOTALS_LABEL_SX = {
  gridArea: "label",
  display: "flex",
  alignItems: "center",
  gap: "var(--space-2)",
};
/**
 * An adjustment on one line: its label, its field, and what it works out to,
 * so the figures read straight down the right edge from Subtotal to Total.
 */
const ADJUSTMENT_SX = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) 136px minmax(96px, max-content)",
  gridTemplateAreas: '"label field figure"',
  alignItems: "center",
  columnGap: "var(--space-3)",
  rowGap: "var(--space-2)",
  // A phone-width summary has no room for all three, so the field drops under its label.
  "@container (max-width: 347px)": {
    gridTemplateColumns: "minmax(0, 1fr) max-content",
    gridTemplateAreas: '"label figure" "field field"',
  },
};
const ADJUSTMENT_FIELD_SX = { gridArea: "field", width: 136 };
const ADJUSTMENT_AMOUNT_SX = {
  gridArea: "figure",
  textAlign: "right" as const,
  fontVariantNumeric: "tabular-nums",
};

export interface AttachedFilesDrawerStateProps {
  open: boolean;
  type: AttachmentDrawerType | null;
  transactionId?: string | null;
  invoiceDetailId?: string | null;
}

const CreateEditInvoice: React.FC = () => {
  const { t } = useTranslation();
  const { isEntitled: featureCustomFields } = useFeatureEntitlements(
    "feature_card_custom_fields",
  );

  const [invoiceIdFromUrl, setInvoiceIdFromUrl] = useState<string | undefined>(
    undefined,
  );
  const [openImportExpenseDrawer, setOpenImportExpenseDrawer] = useState(false);
  const [openInvoicePreviewContainer, setOpenInvoicePreviewContainer] =
    useState(false);
  const [shouldRefreshTransactions, setShouldRefreshTransactions] =
    useState(false);
  const [invoiceNumberExists, setInvoiceNumberExists] = useState(false);
  const [isUrlResolved, setIsUrlResolved] = useState(false); // Track whether the URL has been processed

  const [attachedFilesDrawerState, setAttachedFilesDrawerState] =
    useState<AttachedFilesDrawerStateProps>({
      open: false,
      type: null,
      transactionId: null,
      invoiceDetailId: null,
    });

  const {
    formikInvoice,
    customFieldSettings,
    isAnyLoading,
    specificLoadingStates,
    getMostRecentInvoiceNumber,
    handleCheckInvoiceNumber,
    handleSaveInvoice,
    handleRemoveAsset,
    handleOpenInvoiceHistoryPage,
  } = useInvoiceData({
    invoiceId: invoiceIdFromUrl,
    shouldRefetchAttachments: false,
  });

  const { handleFormikHelperText, handleFormikError, validateAndTouch } =
    useFormikHelpers(formikInvoice);

  useGetParamFromUrl(
    "id",
    (id) => {
      setInvoiceIdFromUrl(id);
      setIsUrlResolved(true); // Mark the URL as resolved
    },
    true,
  );

  const { isInvoiceEditable } = useGetInvoiceStatusDetails(
    formikInvoice.values.status as InvoiceStatus,
  );

  // Ensure the URL is resolved before getting the invoice number
  useEffect(() => {
    if (isUrlResolved && !invoiceIdFromUrl) {
      getMostRecentInvoiceNumber({
        variables: { increment: true },
      });
    }
  }, [isUrlResolved, invoiceIdFromUrl, getMostRecentInvoiceNumber]);

  useEffect(() => {
    if (invoiceIdFromUrl && formikInvoice.values.id && !isInvoiceEditable) {
      handleOpenInvoiceHistoryPage(formikInvoice.values.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formikInvoice.values.status, invoiceIdFromUrl]);

  // GraphQL query
  const [
    getTransactionDetail,
    { data: transactionData, loading: loadingTransaction },
  ] = useLazyQuery(GET_TRANSACTION, {
    fetchPolicy: "network-only",
  });

  const selectedTransaction = transactionData?.transaction;

  useEffect(() => {
    if (attachedFilesDrawerState.transactionId) {
      getTransactionDetail({
        variables: { id: attachedFilesDrawerState.transactionId },
      });
    }
  }, [attachedFilesDrawerState.transactionId, getTransactionDetail]);

  // Handlers for Attachments

  const generateUniqueId = () => {
    return uuidv4();
  };

  const handleLocalFileUpload = (files: LocalImageAsset[]) => {
    const newAttachments = files.map((file) => ({
      id: file?.id,
      assetUrl: file?.assetUrl,
      extension: file?.extension,
      subParentId: attachedFilesDrawerState?.invoiceDetailId || "invoice",
      file: file?.file,
    }));

    formikInvoice.setFieldValue("attachments", [
      ...formikInvoice.values.attachments,
      ...newAttachments,
    ]);

    !attachedFilesDrawerState.open && handleOpenAttachedFilesDrawer({});
  };

  const handleServiceFeeTypeChange = (mode: string) => {
    const newType = mode as InvoiceDiscountType;
    formikInvoice.setFieldValue("serviceFeeType", newType);
    if (newType === InvoiceDiscountType.CREDIT_CARD) {
      formikInvoice.setFieldValue("serviceFee", "");
    }
  };

  const handleSaveSelectedExpenses = useCallback(
    (checkedItems: any[]) => {
      const existingAttachmentIds = new Set(
        formikInvoice.values.attachments.map((attachment) => attachment.id),
      );
      let duplicateFound = false;

      const newDetails = checkedItems.map((item, index) => {
        const amount = parseFloat(item.unitPrice ?? item.amount ?? 0);
        const quantity = parseFloat(item.quantity ?? 1);
        return {
          id: generateUniqueId(),
          invoiceId: formikInvoice.values.id || "",
          order: formikInvoice.values.details.length + index + 1,
          productOrService: item.productOrService,
          description: item.description,
          amount,
          quantity,
          total: parseFloat((amount * quantity).toFixed(2)),
          taxable: false,
        };
      });

      const newAttachments = checkedItems
        .flatMap((item, index) =>
          (item.receipts || []).map((receipt: ImageAsset) => {
            if (existingAttachmentIds.has(receipt.id)) {
              duplicateFound = true;
              return null;
            }
            return {
              ...receipt,
              subParentId: newDetails[index].id, // Attach the detail id as the parentId
              isExisting: true, // flag to differentiate existing attachments (receipts)
              receiptDetailId: receipt.receiptDetailId,
            };
          }),
        )
        .filter((attachment) => attachment !== null);

      if (duplicateFound) {
        toast.error(t("DuplicateFileDetected"));
      }

      formikInvoice.setValues({
        ...formikInvoice.values,
        details: [...formikInvoice.values.details, ...newDetails],
        attachments: [...formikInvoice.values.attachments, ...newAttachments],
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formikInvoice.values],
  );

  const handleRefreshTransaction = useCallback(() => {
    getTransactionDetail({
      variables: { id: attachedFilesDrawerState.transactionId },
    });
    setShouldRefreshTransactions(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachedFilesDrawerState.transactionId]);

  // Handlers for Attached Files Drawer

  const handleOpenAttachedFilesDrawer = ({
    transactionId,
    invoiceDetailId,
  }: {
    transactionId?: string;
    invoiceDetailId?: string;
  }) => {
    setAttachedFilesDrawerState({
      open: true,
      type: transactionId
        ? AttachmentDrawerType.TRANSACTION
        : AttachmentDrawerType.ATTACHMENT,
      transactionId: transactionId || null,
      invoiceDetailId: invoiceDetailId || null,
    });
  };

  const handleCloseAttachedFilesDrawer = () => {
    setAttachedFilesDrawerState({
      open: false,
      type: null,
      transactionId: null,
      invoiceDetailId: null,
    });
  };

  const handleExitInvoicePreview = () => {
    setOpenInvoicePreviewContainer(false);
  };

  const checkInvoiceNumber = async (invoiceNumber: string) => {
    try {
      if (!invoiceNumber) {
        setInvoiceNumberExists(false);
        return;
      }
      const invoiceNumberExists = await handleCheckInvoiceNumber(
        invoiceNumber,
        formikInvoice.values.id,
      );

      invoiceNumberExists
        ? setInvoiceNumberExists(true)
        : setInvoiceNumberExists(false);
    } catch (error: any) {
      console.error(t("Error checking invoice number"), error);
      toast.error(t("Failed to check invoice number"));
    }
  };

  const debouncedCheckInvoiceNumber = useDebouncedAutoSave(
    checkInvoiceNumber,
    DEBOUNCE_TIMEOUT,
  );

  // Attachments to send to the AttachedFilesDrawer (filters attachments on line item id)
  const attachedInvoiceFiles = useMemo(() => {
    let filterAttachmentId = attachedFilesDrawerState.invoiceDetailId || null;
    if (
      attachedFilesDrawerState.type === AttachmentDrawerType.ATTACHMENT &&
      formikInvoice?.values
    ) {
      const attachments =
        formikInvoice?.values.attachments
          .filter(
            (attachment: any) =>
              !filterAttachmentId ||
              attachment.subParentId === filterAttachmentId,
          )
          .map((attachment: any) => ({
            assetUrl: attachment.assetUrl,
            id: attachment.id,
            subParentId: attachment.subParentId || "invoice",
            extension: attachment.extension,
          })) || [];

      return attachments;
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    attachedFilesDrawerState.type,
    formikInvoice.values.attachments,
    attachedFilesDrawerState.invoiceDetailId,
  ]);

  // details for the dropdown in the AttachedFilesDrawer
  const dropdownDetails = useMemo(() => {
    if (
      attachedFilesDrawerState.type === AttachmentDrawerType.ATTACHMENT &&
      formikInvoice?.values
    ) {
      const options = formikInvoice.values.details.map((detail) => ({
        id: detail.id,
        value: `${detail.order}. ${detail.productOrService || ""}`, // Include the order in the value
      }));

      const onSelect = (selectedId: string, assetId: string) => {
        // Find the asset
        const assetIndex = formikInvoice.values.attachments.findIndex(
          (a) => a.id === assetId,
        );

        if (assetIndex !== -1) {
          const updatedAttachments = [...formikInvoice.values.attachments];
          updatedAttachments[assetIndex] = {
            ...updatedAttachments[assetIndex],
            subParentId: selectedId === "invoice" ? "invoice" : selectedId,
          };

          formikInvoice.setFieldValue("attachments", updatedAttachments);
        }
      };

      return {
        options,
        onSelect,
        defaultOptionText: t("Attach to invoice"),
        defaultOptionValue: "invoice",
      };
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachedFilesDrawerState.type, formikInvoice]);

  return (
    <TablePage
      title={t("Send an Invoice")}
      wrapped={false}
      breadcrumbs={[
        {
          label: t("Invoices"),
          // onClick without href, which is the only shape that routes in
          // app. PageHeader calls preventDefault only when href is the "#"
          // placeholder, so a crumb carrying a real href reloads the whole
          // app whether or not a handler is attached. Costs middle-click
          // and copy-link on this step until the package is fixed.
          onClick: () => handleOpenInvoiceHistoryPage(),
        },
        { label: t("Send an Invoice") },
      ]}
    >
      <ScrollArea sx={{ minHeight: "100%", maxHeight: "100%", width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "flex-start",
            gap: "var(--space-5)",
            width: "100%",
            paddingBlockEnd: "var(--space-5)",
          }}
        >
          {/* The form: everything the sender fills in. */}
          <Box
            sx={{
              flex: { xs: "1 1 100%", lg: "1 1 0" },
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              gap: "var(--space-5)",
            }}
          >
            <CustomPanel>
              <Box sx={PANEL_BODY_SX}>
                <Inline
                  align="start"
                  sx={{ columnGap: "var(--space-9)", rowGap: "var(--space-7)" }}
                >
                  <FormField
                    label={t("Recipient")}
                    required
                    errorText={
                      handleFormikHelperText("primaryRecipient") || undefined
                    }
                    sx={FIELD_COLUMN_SX}
                  >
                    <ContactSearchAutoComplete
                      onSelectContact={(recipient) => {
                        if (!recipient) {
                          formikInvoice.setFieldValue("primaryRecipient", null);
                        } else if (
                          !Array.isArray(recipient) &&
                          formikInvoice.values.primaryRecipient?.id !==
                            recipient.id
                        ) {
                          formikInvoice.setFieldValue(
                            "primaryRecipient",
                            recipient,
                          );
                        }
                      }}
                      contactId={formikInvoice.values.primaryRecipient?.id}
                      showAddContactInDropdown={true}
                      customPlaceholder={t("Search saved contacts")}
                      hideLabel
                      status={ContactStatus.Active}
                      required={true}
                      handleBlur={() =>
                        formikInvoice.setFieldTouched("primaryRecipient", true)
                      }
                    />
                  </FormField>

                  <FormField
                    label={t("CC Recipients")}
                    errorText={
                      handleFormikHelperText("secondaryRecipients") || undefined
                    }
                    sx={FIELD_COLUMN_SX}
                  >
                    <ContactSearchAutoComplete
                      onSelectContact={(recipients) =>
                        formikInvoice.setFieldValue(
                          "secondaryRecipients",
                          recipients,
                        )
                      }
                      selectedSecondaryRecipients={
                        formikInvoice.values.secondaryRecipients
                      }
                      showAddContactInDropdown={true}
                      customPlaceholder={t("Search saved contacts")}
                      hideLabel
                      status={ContactStatus.Active}
                      handleBlur={() =>
                        formikInvoice.setFieldTouched(
                          "secondaryRecipients",
                          true,
                        )
                      }
                      multiple={true}
                    />
                  </FormField>

                  <Box sx={FIELD_COLUMN_SX}>
                    <TextField
                      id="invoice-name"
                      fullWidth
                      name="invoiceName"
                      label={t("Invoice Name")}
                      required
                      placeholder={t("InvoiceNamePlaceholder")}
                      value={formikInvoice.values.invoiceName || ""}
                      onChange={formikInvoice.handleChange}
                      onBlur={formikInvoice.handleBlur}
                      errorText={
                        handleFormikHelperText("invoiceName") || undefined
                      }
                    />
                  </Box>

                  <FormField
                    label={t("Invoice Date")}
                    required
                    helperText={t("Date the invoice will be sent on")}
                    errorText={
                      handleFormikHelperText("invoiceDate", {
                        ignoreTouched: true,
                      }) || undefined
                    }
                    sx={FIELD_COLUMN_SX}
                  >
                    <DatePicker
                      // The FormField's errorText alone left the input looking
                      // valid while its own message said otherwise. Both halves,
                      // as AddFundingDrawer wires it.
                      error={handleFormikError("invoiceDate", {
                        ignoreTouched: true,
                      })}
                      value={
                        formikInvoice.values.invoiceDate
                          ? dayjs(formikInvoice.values.invoiceDate)
                          : null
                      }
                      onBlur={() =>
                        formikInvoice.setFieldTouched("invoiceDate", true)
                      }
                      onChange={(date) =>
                        formikInvoice.setFieldValue(
                          "invoiceDate",
                          date ? date.toDate() : null,
                        )
                      }
                    />
                  </FormField>

                  <Box sx={FIELD_COLUMN_SX}>
                    <TextField
                      id="invoice-number"
                      fullWidth
                      name="invoiceNumber"
                      label={t("Invoice Number")}
                      required
                      value={formikInvoice.values.invoiceNumber || ""}
                      onChange={(e) => {
                        formikInvoice.handleChange(e);
                        debouncedCheckInvoiceNumber(e.target.value);
                      }}
                      onBlur={formikInvoice.handleBlur}
                      errorText={
                        handleFormikHelperText("invoiceNumber", {
                          customErrorCheck: () =>
                            invoiceNumberExists
                              ? t("Invoice number already exists")
                              : null,
                        }) || undefined
                      }
                      trailing={
                        specificLoadingStates.loadingInvoiceNumber ? (
                          <CircularProgress size={16} />
                        ) : undefined
                      }
                    />
                  </Box>

                  <Box sx={FIELD_COLUMN_SX}>
                    <TextField
                      id="invoice-po-number"
                      fullWidth
                      name="purchaseOrderId"
                      label={t("PO Number (optional)")}
                      placeholder={t("Optional")}
                      value={formikInvoice.values.purchaseOrderId || ""}
                      onChange={formikInvoice.handleChange}
                      onBlur={formikInvoice.handleBlur}
                    />
                  </Box>

                  <Box sx={FIELD_COLUMN_SX}>
                    <Select
                      label={t("Payment Terms")}
                      required
                      options={PAYMENT_TERMS_OPTIONS.map(
                        ({ value, labelKey }) => ({
                          id: value,
                          label: t(labelKey),
                        }),
                      )}
                      value={formikInvoice.values.paymentTerms || ""}
                      onChange={(value) =>
                        formikInvoice.setFieldValue("paymentTerms", value ?? "")
                      }
                      errorText={
                        handleFormikHelperText("paymentTerms") || undefined
                      }
                    />
                  </Box>

                  <FormField label={t("Allocation")} sx={FIELD_COLUMN_SX}>
                    <AllocationSearchAutoComplete
                      title={null}
                      allocation={formikInvoice.values.allocationName}
                      allocationType={
                        formikInvoice.values.allocationType || undefined
                      }
                      onSelectAllocation={(allocation) => {
                        formikInvoice.setFieldValue("allocationName", {
                          id: allocation?.id,
                          allocationCode: allocation?.allocationCode,
                        });
                        formikInvoice.setFieldValue(
                          "allocationType",
                          allocation?.allocationType,
                        );
                      }}
                    />
                  </FormField>

                  {featureCustomFields &&
                    (formikInvoice?.values?.primaryRecipient?.id ||
                      formikInvoice?.values?.id) && (
                      <FormikCustomFields
                        settings={customFieldSettings}
                        formik={formikInvoice}
                        variant="flat"
                        sx={FIELD_COLUMN_SX}
                      />
                    )}
                </Inline>
              </Box>
            </CustomPanel>

            <CustomPanel>
              <InvoiceItemsTable
                formikInvoice={formikInvoice}
                handleOpenAttachedFilesDrawer={handleOpenAttachedFilesDrawer}
                onExpenseImport={() => setOpenImportExpenseDrawer(true)}
                showImportExpensesButton={
                  !!formikInvoice?.values?.allocationName?.id
                }
              />
            </CustomPanel>

            <CustomPanel>
              <Box sx={PANEL_BODY_SX}>
                <Grid
                  container
                  spacing="var(--space-9)"
                  sx={{ width: "100%", margin: 0 }}
                >
                  <Grid
                    size={{ xs: 12, sm: 6, md: 9 }}
                    sx={{
                      minWidth: 0,
                      display: "flex",
                      flexDirection: "column",
                      gap: "var(--space-6)",
                      alignItems: "flex-start",
                    }}
                  >
                    <Box sx={NOTES_FILL_SX}>
                      <TextField
                        id="invoice-note"
                        fullWidth
                        name="note"
                        label={t("Customer notes")}
                        placeholder={t("Customer viewable notes (optional)")}
                        multiline
                        rows={6}
                        value={formikInvoice.values.note || ""}
                        onChange={formikInvoice.handleChange}
                      />
                    </Box>
                    <Inline>
                      <Button
                        size="small"
                        variant="primary-soft"
                        onClick={() => {
                          handleOpenAttachedFilesDrawer({
                            invoiceDetailId: "invoice",
                          });
                        }}
                      >
                        <HugeiconsIcon icon={Add01Icon} size={16} />
                        {t("Add files")}
                      </Button>
                      {formikInvoice.values.attachments.length > 0 && (
                        <Typography
                          variant="body2"
                          sx={{ color: "var(--color-text-secondary)" }}
                        >
                          {formikInvoice.values.attachments.length}{" "}
                          {formikInvoice.values.attachments.length > 1
                            ? t("files attached")
                            : t("file attached")}
                        </Typography>
                      )}
                    </Inline>
                  </Grid>
                  <Grid
                    size={{ xs: 12, sm: 6, md: 3 }}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "var(--space-3)",
                    }}
                  >
                    <Typography variant="label">
                      {t("Invoice preview")}
                    </Typography>
                    <Card
                      variant="outlined"
                      sx={{
                        position: "relative",
                        overflow: "hidden",
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        padding: "var(--space-4)",
                        backgroundColor: "var(--color-bg-elevated)",
                        // :focus-visible ON the overlay, which is itself the
                        // button. Not :focus-within, which left the scrim stuck
                        // open after the modal returned focus here; and not
                        // :has(:focus-visible), which asks for a focused
                        // DESCENDANT when the only child is aria-hidden at
                        // tabIndex -1, so a keyboard focus never revealed it.
                        [`&:hover .${PREVIEW_OVERLAY_CLASS}, & .${PREVIEW_OVERLAY_CLASS}:focus-visible`]:
                          { opacity: 1, pointerEvents: "auto" },
                      }}
                    >
                      <InvoicePreviewThumbnail invoice={formikInvoice.values} />
                      <Box
                        component="button"
                        type="button"
                        className={PREVIEW_OVERLAY_CLASS}
                        aria-label={t("Open preview")}
                        onClick={() => setOpenInvoicePreviewContainer(true)}
                        sx={{
                          position: "absolute",
                          inset: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "var(--color-bg-overlay)",
                          opacity: 0,
                          transition: "opacity 150ms ease",
                          border: 0,
                          padding: 0,
                          font: "inherit",
                          cursor: "pointer",
                        }}
                      >
                        <Button
                          component="span"
                          variant="default-outlined"
                          tabIndex={-1}
                          aria-hidden
                          sx={{ pointerEvents: "none" }}
                        >
                          {t("Open preview")}
                        </Button>
                      </Box>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            </CustomPanel>
          </Box>

          {/* The summary: what is owed, and the two ways out. */}
          <Box
            sx={{
              flex: { xs: "1 1 100%", lg: "0 0 400px" },
              minWidth: { lg: "400px" },
              // Under the form it stays a totals block on the right, at the rail's width.
              maxWidth: { xs: 400, lg: "none" },
              marginInlineStart: "auto",
            }}
          >
            <CustomPanel>
              <Box sx={PANEL_BODY_SX}>
                <Typography variant="h4">{t("Summary")}</Typography>
                <Stack
                  gap="var(--space-3)"
                  sx={{ containerType: "inline-size" }}
                >
                  <Inline justify="between" gap="var(--space-3)">
                    <Typography
                      variant="body1"
                      sx={{ color: "var(--color-text-secondary)" }}
                    >
                      {t("Subtotal")}
                    </Typography>
                    <Typography variant="body1" sx={ADJUSTMENT_AMOUNT_SX}>
                      {formatCashFull(
                        formikInvoice.values.subtotal,
                        formikInvoice.values.currency,
                      )}
                    </Typography>
                  </Inline>

                  <Box sx={ADJUSTMENT_SX}>
                    <Box sx={TOTALS_LABEL_SX}>
                      <Typography
                        variant="body1"
                        sx={{ color: "var(--color-text-secondary)" }}
                      >
                        {t("Discount")}
                      </Typography>
                    </Box>
                    <Box sx={ADJUSTMENT_FIELD_SX}>
                      <RateOrAmountField
                        id="invoice-discount"
                        name="discountAmount"
                        ariaLabel={t("Discount")}
                        options={rateOrAmountOptions(t)}
                        mode={formikInvoice.values.discountType}
                        amount={formikInvoice.values.discount}
                        error={handleFormikError("discountAmount")}
                        onModeChange={(mode) =>
                          formikInvoice.setFieldValue("discountType", mode)
                        }
                        onAmountChange={(amount) =>
                          formikInvoice.setFieldValue("discount", amount)
                        }
                        onBlur={formikInvoice.handleBlur}
                      />
                    </Box>
                    <Typography variant="body1" sx={ADJUSTMENT_AMOUNT_SX}>
                      {formatCashFull(
                        formikInvoice.values.discountAmount,
                        formikInvoice.values.currency,
                      )}
                    </Typography>
                  </Box>

                  <Box sx={ADJUSTMENT_SX}>
                    <Box sx={TOTALS_LABEL_SX}>
                      <Typography variant="body1" component="span">
                        {t("Tax")}
                      </Typography>
                      <Tooltip title={t("InvoiceTaxTooltip")} placement="top">
                        <HugeiconsIcon
                          icon={InfoIcon}
                          size={14}
                          color="var(--color-text-tertiary)"
                        />
                      </Tooltip>
                    </Box>
                    <Box sx={ADJUSTMENT_FIELD_SX}>
                      <RateOrAmountField
                        id="invoice-tax"
                        name="taxAmount"
                        ariaLabel={t("Tax")}
                        options={rateOrAmountOptions(t)}
                        mode={formikInvoice.values.taxType}
                        amount={formikInvoice.values.tax}
                        error={handleFormikError("taxAmount")}
                        onModeChange={(mode) =>
                          formikInvoice.setFieldValue("taxType", mode)
                        }
                        onAmountChange={(amount) =>
                          formikInvoice.setFieldValue("tax", amount)
                        }
                        onBlur={formikInvoice.handleBlur}
                      />
                    </Box>
                    <Typography variant="body1" sx={ADJUSTMENT_AMOUNT_SX}>
                      {formatCashFull(
                        formikInvoice.values.taxAmount,
                        formikInvoice.values.currency,
                      )}
                    </Typography>
                  </Box>

                  <Box sx={ADJUSTMENT_SX}>
                    <Box sx={TOTALS_LABEL_SX}>
                      <Typography variant="body1" component="span">
                        {t("Service Fee")}
                      </Typography>
                      <Tooltip
                        title={
                          <>
                            {t("ServiceFeeTooltip")}
                            <br />
                            {t("ServiceFeeTooltipCard")}
                          </>
                        }
                        placement="top"
                      >
                        <HugeiconsIcon
                          icon={InfoIcon}
                          size={14}
                          color="var(--color-text-tertiary)"
                        />
                      </Tooltip>
                    </Box>
                    <Box sx={ADJUSTMENT_FIELD_SX}>
                      <RateOrAmountField
                        id="invoice-service-fee"
                        name="serviceFeeAmount"
                        ariaLabel={t("Service Fee")}
                        options={serviceFeeOptions(t)}
                        mode={formikInvoice.values.serviceFeeType}
                        amount={formikInvoice.values.serviceFee}
                        error={handleFormikError("serviceFeeAmount")}
                        disabled={
                          formikInvoice.values.serviceFeeType ===
                          InvoiceDiscountType.CREDIT_CARD
                        }
                        onModeChange={handleServiceFeeTypeChange}
                        onAmountChange={(amount) =>
                          formikInvoice.setFieldValue("serviceFee", amount)
                        }
                        onBlur={formikInvoice.handleBlur}
                      />
                    </Box>
                    <Typography variant="body1" sx={ADJUSTMENT_AMOUNT_SX}>
                      {formatCashFull(
                        formikInvoice.values.serviceFeeAmount,
                        formikInvoice.values.currency,
                      )}
                    </Typography>
                  </Box>

                  <Divider sx={{ marginBlock: "var(--space-4)" }} />

                  <Stack gap="var(--space-2)">
                    <Inline justify="between" gap="var(--space-3)">
                      <Typography variant="h4">{t("Total")}</Typography>
                      <Typography variant="h4">
                        {formatCashFull(
                          formikInvoice.values.total,
                          formikInvoice.values.currency,
                        )}
                      </Typography>
                    </Inline>

                    {handleFormikHelperText("total") && (
                      <Typography
                        variant="caption"
                        sx={{ color: "var(--color-text-danger)" }}
                      >
                        {handleFormikHelperText("total")}
                      </Typography>
                    )}
                  </Stack>
                </Stack>
                {/* One row on the right, the primary last, as onboarding's step actions. */}
                <Inline justify="end" gap="var(--space-3)">
                  <Button
                    variant="default-outlined"
                    disabled={isAnyLoading || invoiceNumberExists}
                    onClick={() => {
                      setOpenInvoicePreviewContainer(true);
                    }}
                  >
                    {t("Preview")}
                  </Button>

                  <Button
                    variant="default-outlined"
                    disabled={isAnyLoading || invoiceNumberExists}
                    onClick={async () => {
                      await handleSaveInvoice(true);
                      setOpenInvoicePreviewContainer(true);
                    }}
                  >
                    {t("Save as Draft")}
                  </Button>

                  <Button
                    variant="primary-filled"
                    disabled={isAnyLoading || invoiceNumberExists}
                    onClick={async () => {
                      const isValid = await validateAndTouch();
                      if (!isValid) return;
                      await handleSaveInvoice();
                      setOpenInvoicePreviewContainer(true);
                    }}
                  >
                    {t("Save")}
                  </Button>
                </Inline>
              </Box>
            </CustomPanel>
          </Box>
        </Box>
      </ScrollArea>

      <LoadingWithoutModal open={loadingTransaction || isAnyLoading} />

      {openInvoicePreviewContainer && (
        <InvoicePreviewContainer
          invoice={formikInvoice.values}
          open={openInvoicePreviewContainer}
          onExit={handleExitInvoicePreview}
        />
      )}

      {formikInvoice?.values?.allocationName?.id && (
        <SelectExpenseDrawer
          open={openImportExpenseDrawer}
          handleCloseSelectExpenseDrawer={() =>
            setOpenImportExpenseDrawer(false)
          }
          allocationCodeId={formikInvoice.values.allocationName.id}
          onSaveItems={handleSaveSelectedExpenses}
          shouldRefreshTransactions={shouldRefreshTransactions}
          setShouldRefreshTransactions={setShouldRefreshTransactions}
        />
      )}

      {attachedFilesDrawerState.open && attachedFilesDrawerState.type && (
        <AttachmentsComponent
          renderType={AttachmentRenderType.DRAWER}
          openAttachmentDrawer={attachedFilesDrawerState.open}
          handleCloseAttachmentDrawer={handleCloseAttachedFilesDrawer}
          drawerType={attachedFilesDrawerState.type}
          drawerTitle={t("Attached Files")}
          dragAndDropHint={t("JPG, PNG,\nWEBP, PDF\n(Max 10 MB)")}
          parentDetails={{
            parentId: "", // purposefully left blank since we dont want to save attachments until the save button is pressed
            parentType: ATTACHMENT_PARENT_TYPE.INVOICE,
            subParentId: attachedFilesDrawerState.invoiceDetailId || "invoice",
          }}
          localAssets={attachedInvoiceFiles}
          onAddAssets={handleLocalFileUpload}
          onRemoveAssets={handleRemoveAsset}
          dropdownDetails={dropdownDetails}
          transactionDetails={{
            transaction: selectedTransaction,
            refetchTransactionData: handleRefreshTransaction,
          }}
        />
      )}
    </TablePage>
  );
};

export default CreateEditInvoice;
